import React from 'react';
import { PipelineCard } from '../../types/pipeline';
import {
  BuildingOfficeIcon,
  CalendarIcon,
  DocumentTextIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface PipelineCardProps {
  card: PipelineCard;
  onClick: () => void;
  stageColor: string;
}

const PipelineCardComponent: React.FC<PipelineCardProps> = ({
  card,
  onClick,
  stageColor
}) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('cardId', card.id);
    e.dataTransfer.setData('fromStage', card.stage);
  };

  // Státusz szín meghatározása
  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      draft: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      sent: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
      pending: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
      accepted: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      rejected: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
      confirmed: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      completed: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      paid: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
      overdue: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
    };
    return statusColors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  // Dokumentum típus ikon
  const getDocumentIcon = () => {
    switch (card.documentType) {
      case 'quotation':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'order':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'delivery_note':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'invoice':
        return <CurrencyDollarIcon className="h-4 w-4" />;
      default:
        return <DocumentTextIcon className="h-4 w-4" />;
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
      style={{ borderLeftColor: stageColor, borderLeftWidth: '4px' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="text-gray-500 dark:text-gray-400">{getDocumentIcon()}</div>
          <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
            {card.documentNumber}
          </span>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(card.status)}`}>
          {card.status}
        </span>
      </div>

      {/* Customer */}
      <div className="flex items-center gap-2 mb-2">
        <BuildingOfficeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{card.customerName}</span>
      </div>

      {/* Amount */}
      {card.totalAmount !== undefined && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <CurrencyDollarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {card.totalAmount.toLocaleString('hu-HU')} {card.currency || 'HUF'}
            </span>
          </div>
        </div>
      )}

      {/* Date */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <CalendarIcon className="h-3 w-3" />
        <span>{new Date(card.createdAt).toLocaleDateString('hu-HU')}</span>
      </div>

      {/* Tags */}
      {card.tags && card.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.tags.slice(0, 2).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs"
            >
              {tag}
            </span>
          ))}
          {card.tags.length > 2 && (
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
              +{card.tags.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Document Count */}
      {card.documentCount && card.documentCount > 0 && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          📎 {card.documentCount} dokumentum
        </div>
      )}
    </div>
  );
};

export default PipelineCardComponent;

// Named export for consistency
export { PipelineCardComponent as PipelineCard };
